/**
 * Helpers centralisés pour les composants de présentation.
 * Toute logique métier ou de formatage doit être placée ici,
 * et non directement dans les vues.
 */

import { IPlan } from '@leedpulse-monorepo/interfaces';
import { CostTypeRef, ECreditType } from '@leedpulse-monorepo/enums';
import type { LeedbucksConfig } from '../domain/use_cases/leedflow/getLeedbucksConfig';
import { colors } from '../domain/types/colors';

// =============================================================================
// Dates
// =============================================================================

/** "12 oct 2027". The French short month ends with a period, dropped here. */
export const formatDate = (ms: number, params: Record<string, unknown> = {}): string => {
  const iLeftMs = ms - Date.now();
  if (params.until && iLeftMs > 0 && iLeftMs < 24 * 60 * 60 * 1000) return `dans ${Math.ceil(iLeftMs / (60 * 60 * 1000))} h`;

  const sDate = new Date(ms).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }).replace('.', '');
  return params.time ? `${sDate} à ${new Date(ms).getHours()}h` : sDate;
};

// =============================================================================
// Leedbucks (credits (kids) united)
// =============================================================================

/** >= 999999 is "unlimited", but we keep the count for stats */
export const LEEDBUCKS_UNLIMITED_THRESHOLD = 999999;

export const isUnlimitedLeedbucks = (leedbucks: number): boolean =>
  leedbucks >= LEEDBUCKS_UNLIMITED_THRESHOLD;

/** The subscription's valid rate when the backend gave one, the global one otherwise; undefined until loaded, 0 for a free action */
export const leedbucksRateOf = (config: LeedbucksConfig, typeRef: CostTypeRef, subscriptionId?: string): number | undefined =>
  (subscriptionId ? config.subscriptions?.[subscriptionId]?.[typeRef]?.unit_value : undefined)
    ?? config.actions?.[typeRef]?.unit_value;

/**
 * Crédits displayed with the equivalent in sms or calls minutes
 */
export const formatPlanLeedbucks = (plan: IPlan, callRate: number | undefined, smsRate: number | undefined): string => {
  const leedbucks = plan.credits?.[ECreditType.leedbucks] ?? 0;
  if (isUnlimitedLeedbucks(leedbucks) || (callRate === 0 && smsRate === 0)) return 'Appels & SMS illimités';
  const minutes = callRate ? Math.floor(leedbucks / callRate) : '-';
  const sms = smsRate ? Math.floor(leedbucks / smsRate) : '-';
  if (callRate === 0) return `Appels illimités ou jusqu'à ${sms} SMS`;
  if (smsRate === 0) return `Jusqu'à ${minutes} min ou SMS illimités`;
  return `Jusqu'à ${minutes} min ou ${sms} SMS`;
};

// =============================================================================
// Types
// =============================================================================

// TODO : @MrWarzo regrouper sous une seule enum a chaque fois car actuellement redefinis
export type PlanType = 'Performance' | 'Premium' | 'Essentiel' | null | undefined;
export type UserStatusType = 'Actif' | 'Inactif' | 'Invité';

export enum SubscriptionStatusType {
  DISPONIBLE = 'Disponible',
  ACTIF = 'Actif',
  RESILIÉ = 'Résilié',
  RESILIATION_EN_COURS = 'Résiliation en cours',
  PAUSED = 'Suspendu',
}

export const subscriptionStatusAsString = (status: SubscriptionStatusType): string => {
  switch (status) {
    case SubscriptionStatusType.DISPONIBLE:
      return 'Disponible';
    case SubscriptionStatusType.ACTIF:
      return 'Actif';
    case SubscriptionStatusType.RESILIÉ:
      return 'Résilié';
    case SubscriptionStatusType.RESILIATION_EN_COURS:
      return 'Résiliation en cours';
    case SubscriptionStatusType.PAUSED:
      return 'En pause';
    default:
      return 'Inconnu';
  }
};

// =============================================================================
// Badge Class Helpers
// =============================================================================

/**
 * Retourne la classe CSS pour un badge de plan (Users).
 */
export const getUserPlanBadgeClass = (plan: PlanType | string, prefix = 'users-page'): string => {
  if (!plan) return `${prefix}__plan-badge--non-assigne`;
  const planLower = plan.toLowerCase();
  return `${prefix}__plan-badge--${planLower}`;
};

/**
 * Retourne la classe CSS pour un badge de plan (Numbers).
 */
export const getNumberPlanBadgeClass = (
  plan: PlanType | string,
  prefix = 'numbers-page'
): string => {
  if (!plan) return `${prefix}__plan-badge--none`;
  const planLower = plan.toLowerCase();
  return `${prefix}__plan-badge--${planLower}`;
};

/**
 * Retourne la classe CSS pour un badge de statut utilisateur.
 */
export const getUserStatusBadgeClass = (
  status: UserStatusType,
  prefix = 'users-page'
): string => {
  const statusLower = status.toLowerCase();
  return `${prefix}__status-badge--${statusLower}`;
};

/**
 * Retourne la classe CSS pour un badge de statut numéro.
 */
export const getSubscriptionStatusBadgeClass = (
  status: SubscriptionStatusType,
  prefix = 'numbers-page'
): string => {
  const statusMap: Record<string, string> = {
    [SubscriptionStatusType.DISPONIBLE]: 'disponible',
    [SubscriptionStatusType.ACTIF]: 'actif',
    [SubscriptionStatusType.RESILIÉ]: 'resilie',
    [SubscriptionStatusType.RESILIATION_EN_COURS]: 'resiliation-en-cours',
    [SubscriptionStatusType.PAUSED]: 'paused',
  };
  return `${prefix}__status-badge--${statusMap[status] || 'actif'}`;
};

// =============================================================================
// Remaining plan credits (lists + profile) — convention: 100 → 0 %, green > 20 %, amber 1-20 %
// =============================================================================

const PLAN_CREDITS_GREEN = colors.planCredits.green;
const PLAN_CREDITS_AMBER = colors.planCredits.amber;

export interface PlanCreditsRemaining {
  isUnlimited: boolean;
  remainingPercent: number; // 0..100
  nearlyEmpty: boolean; // 1..20 %
  exhausted: boolean; // <= 0 %
  amber: boolean; // amber text/gauge
  barColor: string;
}

export const getPlanCreditsRemaining = (total: number, remaining: number, isUnlimited: boolean): PlanCreditsRemaining => {
  if (isUnlimited) {
    return { isUnlimited: true, remainingPercent: 100, nearlyEmpty: false, exhausted: false, amber: false, barColor: PLAN_CREDITS_GREEN };
  }
  const remainingPercent = total > 0 ? Math.max(0, Math.min(100, Math.round((remaining / total) * 100))) : 0;
  const exhausted = total > 0 && remaining <= 0;
  const nearlyEmpty = remainingPercent > 0 && remainingPercent <= 20;
  const amber = nearlyEmpty || exhausted;
  return { isUnlimited: false, remainingPercent, nearlyEmpty, exhausted, amber, barColor: amber ? PLAN_CREDITS_AMBER : PLAN_CREDITS_GREEN };
};

// Overage state for the "Statut" column: 'overage' = PAYG active over the plan, 'disabled' = PAYG off.
export type PlanCreditsOverageState = 'overage' | 'disabled';

export const getPlanCreditsOverageState = (exhausted: boolean, paygEnabled: boolean | undefined): PlanCreditsOverageState | null => {
  if (!exhausted) return null;
  return paygEnabled === false ? 'disabled' : 'overage';
};

// Display state for the "Statut" column, from raw leedbucks (mirrors the Algolia credit_status filter).
export type PlanCreditsDisplayState = 'ready' | 'warning' | 'exhausted' | 'overage' | 'disabled';

export const getPlanCreditsDisplayState = (
  total: number,
  remaining: number,
  paygEnabled: boolean | undefined,
  isUnlimited: boolean,
): PlanCreditsDisplayState => {
  if (isUnlimited) return 'ready';
  if (remaining < 0) return paygEnabled === false ? 'disabled' : 'overage';
  if (remaining <= 0) return paygEnabled === false ? 'disabled' : 'exhausted';
  if (total > 0 && remaining / total < 0.2) return 'warning';
  return 'ready';
};
